// Everything about talking to Betfair EXCEPT the talking.
//
// This module builds requests and reads answers and makes no network call of
// its own, because the two things that call Betfair run in different runtimes
// and cannot share a transport:
//
//   the phone   src/lib/betfair/transport.js, over CapacitorHttp
//   the server  server/providers/betfairExchange.mjs, over node fetch
//
// Betfair's edge (Akamai) answers HTTP 403 to Google Cloud egress addresses, so
// the server path is blocked in production and the phone is the only one that
// works — see HANDOFF. Both still have to agree byte for byte on what a login
// body looks like and what a market book means, and one copy of that in two
// places is two answers to one question waiting to happen. Hence this file.
//
// It is dependency-free apart from the team alias tables, which is the same
// dependency server/providers/canonical.mjs already takes: it is the join key
// the whole app agrees on.
//
// Docs, confirmed 2026-09-14:
//
//   POST https://identitysso.betfair.com/api/login      interactive login
//        AU/NZ accounts: identitysso.betfair.com.au     form-encoded
//        headers: X-Application, Accept, Content-Type
//        body:    username=…&password=…
//        answers: { token, product, status: 'SUCCESS', error: '' }
//        https://betfair-developer-docs.atlassian.net/wiki/spaces/1smk3cen4v3lu3yomq5qye0ni/pages/2687772/Interactive+Login+-+API+Endpoint
//   POST https://identitysso.betfair.com/api/keepAlive  X-Authentication: <token>
//        session is 12 hours outside the UK and Ireland
//        https://betfair-developer-docs.atlassian.net/wiki/spaces/1smk3cen4v3lu3yomq5qye0ni/pages/2687869/Login+Session+Management
//   POST https://api.betfair.com/exchange/betting/rest/v1.0/<operation>/
//        headers: X-Application, X-Authentication, Content-Type, Accept
//        https://betfair-developer-docs.atlassian.net/wiki/spaces/1smk3cen4v3lu3yomq5qye0ni/pages/2687066/Javascript
//   Market data limits: sum(weight) × marketIds ≤ 200 per request, and
//        EX_BEST_OFFERS weighs 5 — so forty market ids a call, and no more.
//        https://betfair-developer-docs.atlassian.net/wiki/spaces/1smk3cen4v3lu3yomq5qye0ni/pages/2687478/Market+Data+Request+Limits
//
// THE PRICE IS THE BEST AVAILABLE BACK PRICE, gross of commission. Deflating it
// is `bookFairNet`'s job (engine/models/sharpAnchor.js), which is where
// engine/outliers.js reads it from too — two places taking 5% off the same
// number would be two different answers to one question.

import { sportFromOddsApiKey, isKnownTeam, teamId, slug } from '../engine/teams.js';

const IDENTITY = (tld) => `https://identitysso.betfair${tld}/api`;

/** The Betting API. One host: the identity hosts are split by jurisdiction, this is not. */
export const BETTING_API = 'https://api.betfair.com/exchange/betting/rest/v1.0';

/** The book key these prices arrive under, everywhere in the app. */
export const BOOK = { key: 'betfair_ex_au', title: 'Betfair Exchange' };

/**
 * A Betfair application key is SIXTEEN letters and digits. A session token is
 * forty-odd characters of base64. They go in different headers and they are
 * easy to swap, because the page that shows you one shows you the other.
 *
 * THIS IS NOT PEDANTRY — IT IS THE BUG THAT COST TWO SESSIONS. `identitysso`
 * does not validate `X-Application` at all; it echoes whatever it is given back
 * as `product`. So a session token pasted into the key field LOGS IN
 * SUCCESSFULLY, the app says "Logged in · session until 12:33 PM", and then
 * every Betting API call is refused because the application key is not one.
 * The only place that refusal can be caught early is here, on the shape.
 *
 * Betfair's own worked example is `VonbT9529IVf9sa5`.
 */
const APP_KEY_RE = /^[A-Za-z0-9]{16}$/;

export const looksLikeAppKey = (key) => APP_KEY_RE.test(String(key ?? '').trim());

/** What to say when it does not. One sentence, in one place; three screens read it. */
export const APP_KEY_NOTE = 'That looks like a session token, not an app key. An app key is 16 letters and digits. Get yours from getDeveloperAppKeys in Betfair’s API-NG visualiser.';

/** Where to get one, spelled out for a punter rather than a developer. */
export const APP_KEY_HELP_URL = 'https://betfair-datascientists.github.io/api/apiappkey/';

/**
 * Australia and New Zealand are a jurisdiction of their own on the identity
 * host, so an AU account cannot log in at `.com` and a `.com` account cannot
 * log in at `.com.au`. This is an Australian app, so the AU host is tried
 * first and `.com` is the fallback — Betfair answers a wrong-jurisdiction
 * account the same way it answers a wrong password, so there is nothing in the
 * first reply to branch on.
 */
export const IDENTITY_HOSTS = ['.com.au', '.com'];

/**
 * Session length outside the UK and Ireland, per the Login & Session
 * Management page. Betfair's login answers with a token and no expiry, so the
 * clock is ours; `keepAlive` on app open is what stops it running out.
 */
export const SESSION_MS = 12 * 60 * 60 * 1000;

/** Weight 5 for EX_BEST_OFFERS against a 200-point ceiling: forty, exactly. */
export const MAX_MARKET_IDS = 40;

/**
 * The same ceiling with TRADED VOLUME asked for as well: nine.
 *
 * `EX_TRADED` weighs 17 and `EX_BEST_OFFERS` weighs 5, and the rule is
 * `sum(weight) x marketIds <= 200` — so 200 / 22 is 9.09 and a batch of ten
 * would be refused whole. Four times as many calls for the same card, which is
 * exactly why volume is asked for only where it can be answered: see
 * `bookPayload`.
 */
export const MAX_MARKET_IDS_TRADED = 9;

/** How many market ids one book call may carry, given what it is asking for. */
export const batchSizeFor = ({ traded = false } = {}) =>
  (traded ? MAX_MARKET_IDS_TRADED : MAX_MARKET_IDS);

/** How many MATCH_ODDS markets one catalogue call asks for. */
export const MAX_RESULTS = 200;

/** The default window when a caller names none: now to the end of the day after next. */
export const WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

/** ISO 8601 to the second, which is what every Betfair time field is. */
export const iso = (ms) => new Date(Math.floor(ms / 1000) * 1000).toISOString().replace('.000Z', 'Z');

/** When a session started now would run out. */
export const expiryFrom = (now) => iso(now + SESSION_MS);

/**
 * Betfair's event types, by name, from Betfair's own Automation Hub table
 * (betfair-datascientists.github.io/api/apiPythontutorial/, read 2026-09-14).
 * Ten for the board, plus the two racing carries.
 *
 * HARNESS HAS NO EVENT TYPE OF ITS OWN IN AUSTRALIA: Betfair files the trots
 * inside Horse Racing (7), so a market under 7 is a gallops race OR a harness
 * race and nothing in the market says which. That is why `toRaces` marks a 7 as
 * `horse-or-harness` and leaves it to the card — which knows — to decide.
 */
export const EVENT_TYPE_IDS = Object.freeze({
  'Australian Rules': '61420',
  'Rugby League': '1477',
  'Rugby Union': '5',
  Soccer: '1',
  Basketball: '7522',
  Tennis: '2',
  Baseball: '7511',
  'Ice Hockey': '7524',
  'American Football': '6423',
  Cricket: '4',
  'Horse Racing': '7',
  'Greyhound Racing': '4339',
});

/**
 * The Odds API spells a league `<category>_<league>` — `aussierules_afl`,
 * `soccer_australia_aleague`, `basketball_nbl` — and Betfair's event type is
 * the CATEGORY, not the league. So the prefix is the whole of the mapping, and
 * it works for the provider's seventy-odd leagues rather than the twelve the
 * app ships knowing about.
 */
const BY_PREFIX = Object.freeze([
  ['aussierules_', EVENT_TYPE_IDS['Australian Rules']],
  ['rugbyleague_', EVENT_TYPE_IDS['Rugby League']],
  ['rugbyunion_', EVENT_TYPE_IDS['Rugby Union']],
  ['soccer_', EVENT_TYPE_IDS.Soccer],
  ['basketball_', EVENT_TYPE_IDS.Basketball],
  ['tennis_', EVENT_TYPE_IDS.Tennis],
  ['baseball_', EVENT_TYPE_IDS.Baseball],
  ['icehockey_', EVENT_TYPE_IDS['Ice Hockey']],
  ['americanfootball_', EVENT_TYPE_IDS['American Football']],
  ['cricket_', EVENT_TYPE_IDS.Cricket],
]);

/**
 * Racing's own categories, and the event type that carries each.
 *
 * Racing never reaches `BY_PREFIX`: it has no Odds API sport key at all — the
 * card comes from PuntersEdge — so the category names the event type directly
 * and the two maps can never fight.
 */
export const RACING_CATEGORIES = Object.freeze(['horse', 'harness', 'greyhound']);

export const RACING_EVENT_TYPE_IDS = Object.freeze({
  horse: EVENT_TYPE_IDS['Horse Racing'],
  harness: EVENT_TYPE_IDS['Horse Racing'],
  greyhound: EVENT_TYPE_IDS['Greyhound Racing'],
});

/** Australia and New Zealand, which is the whole of the card. */
export const RACING_COUNTRIES = Object.freeze(['AU', 'NZ']);

/** A day of racing: everything still to jump between now and this time tomorrow. */
export const RACING_WINDOW_MS = 24 * 60 * 60 * 1000;

/** A full AU/NZ day is a few hundred WIN markets; 1000 is the ceiling, not the plan. */
export const RACING_MAX_RESULTS = 1000;

/**
 * The event types behind a list of categories, each once.
 *
 * Horse and harness are the same id, so asking for both is ONE catalogue call
 * rather than two identical ones. An empty list means the lot.
 */
export function racingEventTypes(categories = []) {
  const wanted = categories.length ? categories : RACING_CATEGORIES;
  const out = [];
  for (const category of wanted) {
    const id = RACING_EVENT_TYPE_IDS[String(category ?? '').toLowerCase()];
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/** The Betfair event type for an Odds API sport key, or null. */
export function eventTypeIdFor(sportKey) {
  const key = String(sportKey ?? '');
  for (const [prefix, id] of BY_PREFIX) if (key.startsWith(prefix)) return id;
  return null;
}

/** The punter's boards, grouped by the Betfair event type that carries them. */
export function groupSports(sports = []) {
  const out = {};
  for (const sport of sports) {
    const id = eventTypeIdFor(sport);
    if (!id) continue;
    (out[id] ??= []).push(sport);
  }
  return out;
}

/** `items` in runs of at most `size`, in order. */
export function batches(items, size = MAX_MARKET_IDS) {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

// --- what a request looks like --------------------------------------------------

/**
 * The interactive login, as one request description: `{ url, headers, body }`.
 * The body is pre-encoded rather than left as an object, because the two
 * transports serialise a form body differently and this is the half that has
 * to be identical.
 */
export function loginRequest({ username, password, appKey, host = IDENTITY_HOSTS[0] }) {
  return {
    url: `${IDENTITY(host)}/login`,
    headers: {
      'X-Application': appKey,
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({ username, password }).toString(),
  };
}

/** The same, for pushing a session's expiry out. No body at all. */
export function keepAliveRequest({ appKey, session, host = IDENTITY_HOSTS[0] }) {
  return {
    url: `${IDENTITY(host)}/keepAlive`,
    headers: {
      'X-Application': appKey,
      'X-Authentication': session,
      Accept: 'application/json',
    },
    body: null,
  };
}

/** One Betting API operation: `{ url, headers, body }` with the payload as JSON. */
export function bettingRequest(operation, payload, { appKey, session }) {
  return {
    url: `${BETTING_API}/${operation}/`,
    headers: {
      'X-Application': appKey,
      'X-Authentication': session,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  };
}

/** The MATCH_ODDS catalogue for one Betfair category, over one window. */
export const cataloguePayload = ({ eventTypeId, from, to, maxResults = MAX_RESULTS }) => ({
  filter: {
    eventTypeIds: [eventTypeId],
    marketTypeCodes: ['MATCH_ODDS'],
    marketStartTime: { from, to },
  },
  marketProjection: ['EVENT', 'RUNNER_DESCRIPTION', 'EVENT_TYPE', 'MARKET_START_TIME'],
  sort: 'FIRST_TO_START',
  maxResults,
});

/**
 * The WIN catalogue for one racing category, over the next twenty-four hours.
 *
 * `RUNNER_METADATA` is the whole reason racing needs its own payload: it is
 * where `CLOTH_NUMBER` lives, and the cloth number is what a runner joins on.
 * The runner NAME carries it too ("4. Winx"), but a name is a spelling and a
 * cloth number is an identifier — see `toRaces`.
 *
 * It is a CATALOGUE projection, so it costs nothing against the market-data
 * weight that `MAX_MARKET_IDS` is arithmetic on: the book call is unchanged and
 * still forty ids.
 */
export const racingCataloguePayload = ({
  eventTypeId, from, to, now = Date.now(), maxResults = RACING_MAX_RESULTS,
}) => ({
  filter: {
    eventTypeIds: [eventTypeId],
    marketTypeCodes: ['WIN'],
    marketCountries: [...RACING_COUNTRIES],
    marketStartTime: { from: from ?? iso(now), to: to ?? iso(now + RACING_WINDOW_MS) },
  },
  marketProjection: ['EVENT', 'RUNNER_DESCRIPTION', 'RUNNER_METADATA', 'EVENT_TYPE', 'MARKET_START_TIME'],
  sort: 'FIRST_TO_START',
  maxResults,
});

/**
 * Prices for up to MAX_MARKET_IDS markets. Anything added here divides the batch.
 *
 * `traded: true` asks for `EX_TRADED` beside the offers, which is how a market's
 * traded ladder comes back — and it is OFF by default for two reasons that both
 * matter. It costs 17 weight against 5, so the batch drops from forty ids to
 * nine (`batchSizeFor`) and a full AU card goes from a handful of calls to
 * dozens; and a DELAYED app key, which is the only key this app has ever run
 * on, answers it with nothing. `totalMatched` — the one figure the trust gate
 * actually reads — rides on the market itself whatever the projection says, so
 * the gate works either way and `toRaces` keeps it where it finds it.
 */
export const bookPayload = (marketIds, { traded = false } = {}) => ({
  marketIds,
  priceProjection: { priceData: traded ? ['EX_BEST_OFFERS', 'EX_TRADED'] : ['EX_BEST_OFFERS'] },
});

/**
 * The simplest request the Betting API takes: no filter, no projection, a
 * couple of dozen rows back. It exists to tell a refusal of the REQUEST apart
 * from a refusal of the CALLER — if this is refused too, nothing about the
 * payload is the problem.
 */
export const eventTypesPayload = () => ({ filter: {} });

/** The cheapest call that proves a key and a session at once. */
export const probePayload = (now) => ({
  filter: {
    eventTypeIds: [EVENT_TYPE_IDS['Australian Rules'], EVENT_TYPE_IDS['Rugby League'], EVENT_TYPE_IDS.Soccer],
    marketTypeCodes: ['MATCH_ODDS'],
    marketStartTime: { from: iso(now), to: iso(now + WINDOW_MS) },
  },
  marketProjection: ['EVENT'],
  sort: 'FIRST_TO_START',
  maxResults: 1,
});

// --- what an answer means -------------------------------------------------------

/**
 * Is this one of Betfair's own enum strings?
 *
 * The shape check is what makes an error code safe to put on a screen or in a
 * log: `INVALID_USERNAME_OR_PASSWORD` is Betfair's vocabulary, and nothing a
 * punter typed can ever match it.
 */
export const isBetfairCode = (value) => typeof value === 'string' && /^[A-Z_]{3,60}$/.test(value);

/**
 * A Betfair API error code — `INVALID_SESSION_INFORMATION`, `TOO_MUCH_DATA`,
 * `INVALID_APP_KEY` — or null.
 */
export function apiErrorCode(body) {
  const code = body?.detail?.APINGException?.errorCode ?? body?.detail?.exceptionname ?? null;
  return isBetfairCode(code) ? code : null;
}

// --- saying what went wrong, without saying anything secret ----------------------
//
// A 403 with no APINGException in it is not Betfair's API talking: it is
// something in FRONT of the API — a bot manager, an edge rule, a WAF — and
// those answer in HTML with a reference number on them. `betfair/… : HTTP 403`
// told us nothing and cost a round trip through the owner's phone to learn
// nothing twice, so a refusal now carries a short, scrubbed piece of whatever
// the body actually said.

/**
 * Anything token-shaped: twenty or more characters of the base64/hex alphabet.
 *
 * A session token, an app key and a JWT all match; "Access Denied" and
 * "Reference #18.7dd6dd17" do not. It is deliberately blunt — a diagnostic
 * that might quote a credential is worse than no diagnostic.
 */
const TOKENISH = /[A-Za-z0-9+/=]{20,}/g;

export const redactTokens = (text) => String(text ?? '').replace(TOKENISH, '[redacted]');

/** Akamai stamps a reference on a refusal, and it is the one thing support can trace. */
const REFERENCE = /Reference\s*#\s*[\w.-]+/i;

/**
 * A short, safe piece of a failure body: the `<title>` if it is HTML, else the
 * first `max` characters of it with the tags taken out — plus the edge's
 * reference number when there is one. Everything token-shaped is redacted
 * first, and this only ever reads a RESPONSE, never a request.
 */
export function bodyExcerpt(raw, { max = 120 } = {}) {
  const text = typeof raw === 'string' ? raw : '';
  if (!text.trim()) return '';
  const title = /<title[^>]*>([\s\S]{0,200}?)<\/title>/i.exec(text)?.[1];
  const head = (title ?? text)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
  const reference = REFERENCE.exec(text)?.[0]?.replace(/\s+/g, ' ');
  const parts = [redactTokens(head)];
  if (reference && !head.includes(reference)) parts.push(redactTokens(reference));
  return parts.filter(Boolean).join(' · ');
}

/**
 * Which box in front of Betfair answered, if the response said. `server` and
 * `x-akamai-*` are the two that name an edge; nothing else is read, and a
 * request header is never touched.
 */
export function edgeNote(headers) {
  if (!headers || typeof headers !== 'object') return '';
  const seen = [];
  for (const [name, value] of Object.entries(headers)) {
    const key = String(name).toLowerCase();
    if (key !== 'server' && !key.startsWith('x-akamai-')) continue;
    const text = redactTokens(String(value ?? '')).slice(0, 60).trim();
    if (text) seen.push(`${key}=${text}`);
  }
  return seen.slice(0, 3).join(' · ');
}

/**
 * The one sentence a punter reads when Betfair refuses something, built the
 * same way on the phone and on the server:
 *
 *   Betfair refused listMarketCatalogue (HTTP 403 · Access Denied · Reference #18.abc)
 *
 * `code` wins over the status when Betfair's own API answered, because
 * `INVALID_SESSION_INFORMATION` is an instruction and `HTTP 400` is not.
 */
export function refusalSentence({ operation, status = 0, code = null, excerpt = '', edge = '' }) {
  const why = [code ?? (status > 0 ? `HTTP ${status}` : 'no answer'), excerpt, edge].filter(Boolean);
  return `Betfair refused ${operation} (${why.join(' · ')})`;
}

/**
 * An identity answer (login or keepAlive) → `{ token, code }`.
 *
 * `token` is set only on a real success. `code` is Betfair's own enum when it
 * gave one, so a caller can tell a wrong password from a locked account
 * without this module deciding what either means.
 */
export function readIdentity(body, { fallbackToken = '' } = {}) {
  const token = typeof body?.token === 'string' && body.token ? body.token : '';
  if (body?.status === 'SUCCESS') return { token: token || fallbackToken, code: null };
  return { token: '', code: isBetfairCode(body?.error) ? body.error : null };
}

// --- catalogue + book → the board's own event shape --------------------------------

/** Betfair's own name for a runner with no team: the draw, however it is spelled. */
const isDraw = (name) => /^(the\s+)?draw$/i.test(String(name ?? '').trim());

/** The longest price anyone is offering to lay us — the best available BACK price. */
export function bestBack(runner) {
  let best = null;
  for (const offer of runner?.ex?.availableToBack ?? []) {
    const price = Number(offer?.price);
    if (!Number.isFinite(price) || price <= 1) continue;
    if (best === null || price > best) best = price;
  }
  return best;
}

/**
 * Which of the punter's boards a market belongs to.
 *
 * One Betfair event type carries several Odds API leagues — soccer is one id
 * for the Premier League, the A-League and the Champions League at once — and
 * a row filed under the wrong league gets a matchKey of its own and lands on
 * the board as a phantom match nobody else is pricing. So the teams decide:
 * whichever candidate league's alias table (engine/teams.js) knows one of
 * these two clubs is the league this is. Where nothing recognises them and
 * there is more than one candidate, the market is DROPPED rather than guessed
 * at — a missing exchange column is a smaller lie than an invented match.
 */
export function sportKeyFor(candidates, home, away) {
  for (const key of candidates) {
    const sport = sportFromOddsApiKey(key);
    if (sport && (isKnownTeam(sport, home) || isKnownTeam(sport, away))) return key;
  }
  return candidates.length === 1 ? candidates[0] : null;
}

/**
 * Which runner is the home side.
 *
 * Betfair names an event "Home v Away" and sorts the MATCH_ODDS runners the
 * same way, so `sortPriority` alone would usually do. The event name is read
 * first anyway, through the same alias table the rest of the app joins on:
 * where the two agree nothing changes, and where they do not, the name is the
 * half a punter can check.
 */
function orderSides(runners, eventName, sport) {
  const byPriority = [...runners].sort((a, b) => (a.sortPriority ?? 99) - (b.sortPriority ?? 99));
  const halves = String(eventName ?? '').split(/\s+(?:v|vs\.?)\s+/i);
  if (halves.length === 2 && sport) {
    const homeId = teamId(sport, halves[0]);
    const found = byPriority.find((r) => teamId(sport, r.name) === homeId);
    if (found && homeId) return [found, ...byPriority.filter((r) => r !== found)];
  }
  return byPriority;
}

/**
 * Catalogue + book → the Odds API event shape the whole app already reads.
 *
 * `books` is a Map (or a plain object) keyed by marketId. A market nobody is
 * offering on, or one with fewer than two priced runners, is left out
 * entirely: a one-sided quote is not a market, and `bookFair` would refuse to
 * devig it anyway.
 */
export function toEvents(markets, books, { candidates = {}, unattributed = () => {} } = {}) {
  const events = [];
  for (const market of markets ?? []) {
    const book = books?.get?.(market?.marketId) ?? books?.[market?.marketId];
    if (!book || (book.status && book.status !== 'OPEN')) continue;

    const priced = new Map();
    for (const runner of book.runners ?? []) {
      if (runner?.status && runner.status !== 'ACTIVE') continue;
      const price = bestBack(runner);
      if (price !== null) priced.set(runner.selectionId, price);
    }
    if (priced.size < 2) continue;

    const named = (market.runners ?? [])
      .filter((r) => priced.has(r?.selectionId))
      .map((r) => ({ selectionId: r.selectionId, name: String(r.runnerName ?? ''), sortPriority: r.sortPriority }));
    const draw = named.find((r) => isDraw(r.name)) ?? null;
    const sides = named.filter((r) => r !== draw);
    if (sides.length !== 2) continue;

    const eventTypeId = String(market.eventType?.id ?? '');
    const forType = candidates[eventTypeId] ?? [];
    const sportKey = sportKeyFor(forType, sides[0].name, sides[1].name);
    if (!sportKey) {
      unattributed(market.marketId);
      continue;
    }
    const [home, away] = orderSides(sides, market.event?.name, sportFromOddsApiKey(sportKey));

    const outcomes = [
      { name: home.name, price: priced.get(home.selectionId) },
      { name: away.name, price: priced.get(away.selectionId) },
      ...(draw ? [{ name: 'Draw', price: priced.get(draw.selectionId) }] : []),
    ];

    events.push({
      id: String(market.marketId),
      sport_key: sportKey,
      sport_title: market.eventType?.name ?? sportKey,
      commence_time: market.marketStartTime ?? market.event?.openDate ?? null,
      home_team: home.name,
      away_team: away.name,
      bookmakers: [{ key: BOOK.key, title: BOOK.title, markets: [{ key: 'h2h', outcomes }] }],
    });
  }
  return events;
}

// --- WIN catalogue + book → a race ------------------------------------------------
//
// Racing is not two teams, so `toEvents` cannot carry it: it drops anything that
// is not a two-sided market, and a twelve-runner field is eleven sides too many.
// What comes out of here is NOT the board's event shape either — it is the
// exchange's own view of a race, which `src/lib/racing/match-exchange.js` then
// joins onto the card PuntersEdge priced. One module reads Betfair, another
// decides what is the same race; a matcher that also parsed market names would
// be two jobs in one place.

/** "Randwick (AUS) 15th Sep" → "Randwick". Betfair stamps the date and country on. */
const venueFromName = (name) => String(name ?? '')
  .replace(/\s*\([A-Z]{2,3}\)\s*/g, ' ')
  .replace(/\s+\d{1,2}(st|nd|rd|th)\s+\w+$/i, '')
  .replace(/\s+/g, ' ')
  .trim();

/** "R5 1200m Hcap" → 5. A market whose number cannot be read is not a race we can place. */
function raceNumberFrom(marketName) {
  const text = String(marketName ?? '');
  const n = /^\s*R(?:ace)?\s*(\d{1,2})\b/i.exec(text)?.[1] ?? /\bRace\s+(\d{1,2})\b/i.exec(text)?.[1];
  const number = Number(n);
  return Number.isInteger(number) && number > 0 ? number : null;
}

/**
 * The cloth number, from the metadata first and the name second.
 *
 * `RUNNER_METADATA.CLOTH_NUMBER` is the identifier; the `"4. Winx"` prefix on
 * the runner name is the same number as a spelling, and it is there on every
 * AU racing market whether the metadata came through or not. Greyhounds carry
 * the box number in the same field.
 */
function clothFrom(runner) {
  const meta = Number(runner?.metadata?.CLOTH_NUMBER);
  if (Number.isInteger(meta) && meta > 0) return meta;
  const prefixed = Number(/^\s*(\d{1,2})\s*[.)]\s*/.exec(String(runner?.runnerName ?? ''))?.[1]);
  return Number.isInteger(prefixed) && prefixed > 0 ? prefixed : null;
}

/** The runner name without the cloth prefix: "4. Winx" → "Winx". */
const runnerNameFrom = (runnerName) => String(runnerName ?? '').replace(/^\s*\d{1,2}\s*[.)]\s*/, '').trim();

/**
 * Catalogue + book → the exchange's races.
 *
 *   { marketId, venue, venueSlug, raceNumber, startTime, category, totalMatched,
 *     runners: [{ cloth, name, bestBack, status }] }
 *
 * `category` is `greyhound` for event type 4339 and **`horse-or-harness`** for
 * 7 — see the note on `EVENT_TYPE_IDS`. Nothing here guesses which of the two a
 * market is; the card knows, and `matchRace` reads both.
 *
 * `REMOVED` runners are dropped: a scratching is not a runner, and the card has
 * already taken it out of `selections`. A runner nobody is offering on is KEPT
 * with a null price — it is still part of the field, and the thing that appends
 * prices skips the nulls. A market with no book, a book that is not OPEN
 * (racing suspends at the jump), a market with no readable venue or race
 * number, and a race with nothing left in it are all skipped and counted: a
 * price on the wrong race is worse than no price at all.
 */
export function toRaces(markets, books, { dropped = () => {} } = {}) {
  const races = [];
  for (const market of markets ?? []) {
    const book = books?.get?.(market?.marketId) ?? books?.[market?.marketId];
    if (!book || (book.status && book.status !== 'OPEN')) {
      dropped(market?.marketId);
      continue;
    }

    const venue = venueFromName(market.event?.venue || market.event?.name);
    const raceNumber = raceNumberFrom(market.marketName);
    const startTime = market.marketStartTime ?? market.event?.openDate ?? null;
    if (!venue || !raceNumber || !startTime) {
      dropped(market?.marketId);
      continue;
    }

    const priced = new Map();
    for (const runner of book.runners ?? []) {
      if (runner?.status === 'REMOVED') continue;
      priced.set(runner?.selectionId, runner);
    }

    const runners = [];
    for (const runner of market.runners ?? []) {
      const quote = priced.get(runner?.selectionId);
      if (!quote) continue;
      runners.push({
        cloth: clothFrom(runner),
        name: runnerNameFrom(runner.runnerName),
        bestBack: bestBack(quote),
        status: quote.status ?? 'ACTIVE',
      });
    }
    if (!runners.length) {
      dropped(market?.marketId);
      continue;
    }

    races.push({
      marketId: String(market.marketId),
      venue,
      venueSlug: slug(venue),
      raceNumber,
      startTime,
      // HOW MUCH MONEY HAS GONE THROUGH THIS MARKET, or null.
      //
      // The one number the exchange-trust gate reads (lib/racing/trust.js): a
      // Betfair price is the price the money settled on once the money is
      // there, and $400 in a market at noon is two people, not a market. It is
      // a field on the MarketBook rather than on a runner, so one figure covers
      // the race, and a DELAYED app key answers it with nothing — which is the
      // key this app runs on, and why null is a first-class answer here rather
      // than a zero. Zero is read as nothing too: a market with no money in it
      // and a key that cannot see the money are the same thing to the gate.
      totalMatched: Number.isFinite(Number(book.totalMatched)) && Number(book.totalMatched) > 0
        ? Number(book.totalMatched)
        : null,
      category: String(market.eventType?.id ?? '') === EVENT_TYPE_IDS['Greyhound Racing']
        ? 'greyhound'
        : 'horse-or-harness',
      runners,
    });
  }
  return races;
}
